import { createFileRoute } from "@tanstack/react-router";
import { Check } from "lucide-react";

import { Cabecera } from "@/components/propuesta/cabecera";
import { Estado, Punto } from "@/components/propuesta/piezas";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export const Route = createFileRoute("/_app/piezas")({ component: Piezas });

// ===========================================================================
// Las piezas del diseño (lámina 6).
//
// No es una pantalla del módulo: es la página que explica por qué las otras
// son como son. Está dentro de la aplicación —y no en un PDF— porque el día
// que esto se funda con el ERP, quien tenga que decidir si una tabla nueva
// lleva punto y palabra o solo color necesita poder abrirla y mirarla, no
// buscar un adjunto en un correo de hace ocho meses.
//
// Los colores de aquí son los mismos tokens que usan las pantallas. Si uno
// cambia, esta página cambia sola; si esta página miente, es que alguien
// escribió un color a mano en algún sitio.
// ===========================================================================

const ORIGEN = [
  {
    tono: "acc" as const,
    de: "Se queda",
    titulo: "Lo que ya existe",
    subtitulo: "La base sobre la que se monta todo lo demás.",
    puntos: [
      [
        "Azul marino y cyan.",
        "La identidad no cambia: quien ya usa la ventana la reconoce.",
      ],
      [
        "El color de estado va aparte del de marca.",
        "Verde, ámbar, rojo y azul solo significan estado; nunca decoran.",
      ],
      [
        "Castellano llano, sin jerga.",
        "«Necesita una persona», no «pending manual review».",
      ],
      [
        "Las reglas, explicadas en la propia pantalla:",
        "por qué el sistema decidió lo que decidió.",
      ],
    ],
  },
  {
    tono: "ok" as const,
    de: "Se toma de SAP S/4HANA",
    titulo: "Densidad y jerarquía",
    subtitulo:
      "Lo que hace que una pantalla con miles de filas siga siendo legible.",
    puntos: [
      [
        "Barra global arriba",
        "con buscador único y avisos: siempre en el mismo sitio.",
      ],
      [
        "Tiles de arranque",
        "con el número grande primero y el detalle debajo.",
      ],
      ["Ficha de objeto:", "lo importante fijo arriba y el detalle debajo."],
      [
        "Barra de acciones anclada al pie:",
        "los botones que deciden nunca se pierden al bajar.",
      ],
      ["Estado con punto y palabra,", "no solo con color."],
    ],
  },
  {
    tono: "loan" as const,
    de: "Se toma de Odoo",
    titulo: "La forma de trabajar el día a día",
    subtitulo: "Lo que convierte una lista en una mesa de trabajo.",
    puntos: [
      ["Migas de pan:", "siempre se sabe dónde se está y cómo volver."],
      [
        "Buscador por facetas:",
        "cada filtro queda como una etiqueta que se quita con un clic.",
      ],
      [
        "Vista sencilla y vista completa:",
        "la misma verdad, dos niveles de detalle.",
      ],
      [
        "Barra de etapas en la ficha:",
        "se ve el camino entero y en cuál está.",
      ],
      [
        "Seguimiento al pie de cada ficha:",
        "quién dijo qué, cuándo, y qué hizo el sistema.",
      ],
    ],
  },
];

const COLORES = [
  { nombre: "Marino", hex: "#0E2438", clase: "bg-[#0E2438]" },
  { nombre: "Cyan · acento", hex: "#0FB5CE", clase: "bg-[#0FB5CE]" },
  { nombre: "Fondo", hex: "#EDF1F6", clase: "bg-[#EDF1F6] border" },
  { nombre: "Texto", hex: "#14304B", clase: "bg-[#14304B]" },
  { nombre: "Conciliado", hex: "#0E9F6E", clase: "bg-[#0E9F6E]" },
  { nombre: "Por decidir", hex: "#C77A08", clase: "bg-[#C77A08]" },
  { nombre: "Sin respaldo", hex: "#D0342C", clase: "bg-[#D0342C]" },
  { nombre: "Préstamo", hex: "#2E6FE0", clase: "bg-[#2E6FE0]" },
  { nombre: "Neutro", hex: "#64748B", clase: "bg-[#64748B]" },
];

const ESTADOS = [
  { tono: "ok" as const, texto: "Conciliado" },
  { tono: "ok" as const, texto: "Exacta" },
  { tono: "warn" as const, texto: "Probable" },
  { tono: "warn" as const, texto: "Cruce de moneda" },
  { tono: "warn" as const, texto: "En partes" },
  { tono: "bad" as const, texto: "Sin respaldo" },
  { tono: "loan" as const, texto: "Préstamo" },
  { tono: "neutro" as const, texto: "Sin candidatos" },
];

function Piezas() {
  return (
    <>
      <Cabecera migas={["Conciliación"]} titulo="De dónde viene cada pieza" />

      <p className="-mt-2 max-w-3xl text-[13px] text-muted-foreground">
        Lo que se queda del sistema actual, lo que se toma de SAP S/4HANA y lo
        que se toma de Odoo. Esta página se lee sola: los colores que muestra
        son los que usan las pantallas.
      </p>

      <div className="grid gap-3.5 lg:grid-cols-3">
        {ORIGEN.map((o) => (
          <Card key={o.de}>
            <CardHeader className="space-y-2">
              <Estado tono={o.tono}>{o.de}</Estado>
              <CardTitle className="font-semibold text-base">
                {o.titulo}
              </CardTitle>
              <p className="text-[12.5px] text-muted-foreground">
                {o.subtitulo}
              </p>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2.5 text-[12.5px]">
                {o.puntos.map(([fuerte, resto]) => (
                  <li key={fuerte} className="flex gap-2">
                    <Punto tono={o.tono} />
                    <span>
                      <b>{fuerte}</b>{" "}
                      <span className="text-muted-foreground">{resto}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-3.5 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="font-medium text-base">
              <span className="mr-2 font-semibold text-[11px] text-muted-foreground uppercase tracking-wider">
                Color
              </span>
              Identidad y estado, separados a propósito
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
              {COLORES.map((c) => (
                <div key={c.hex}>
                  <div className={`h-14 rounded-md ${c.clase}`} />
                  <div className="mt-1.5 font-medium text-[12px]">
                    {c.nombre}
                  </div>
                  <div className="font-mono text-[11px] text-muted-foreground">
                    {c.hex}
                  </div>
                </div>
              ))}
            </div>
            <p className="border-t pt-3 text-[12.5px]">
              <b>
                Un movimiento en rojo está sin respaldo; nunca es rojo porque
                toque.
              </b>{" "}
              <span className="text-muted-foreground">
                Por eso el cyan de la marca no aparece jamás en una fila de la
                tabla: si el color de identidad y el de estado se mezclan, quien
                mira deja de fiarse de los dos.
              </span>
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-medium text-base">
              <span className="mr-2 font-semibold text-[11px] text-muted-foreground uppercase tracking-wider">
                Estado
              </span>
              Punto, palabra y color, siempre juntos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {ESTADOS.map((e) => (
                <Estado key={e.texto} tono={e.tono}>
                  {e.texto}
                </Estado>
              ))}
            </div>
            <p className="text-[12.5px] text-muted-foreground">
              Quien no distingue el verde del rojo —uno de cada doce hombres—
              sigue leyendo la palabra. Y la palabra dice qué pasa, no cuánto de
              seguro está el sistema:{" "}
              <b className="text-foreground">
                «probable» es un estado, no una nota
              </b>
              .
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-3.5 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="font-semibold text-[11px] text-muted-foreground uppercase tracking-wider">
              Botones
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2.5">
            <Button className="w-full bg-cyan-600 hover:bg-cyan-700">
              <Check className="mr-2 size-4" />
              Confirmar y conciliar
            </Button>
            <Button variant="outline" className="w-full">
              Elegir otro voucher
            </Button>
            <Button
              variant="outline"
              className="w-full border-red-600/40 text-red-700 hover:bg-red-600/10 dark:text-red-400"
            >
              No es este voucher
            </Button>
            <Button variant="ghost" className="w-full">
              Dejar para después
            </Button>
            <p className="pt-1 text-[12px] text-muted-foreground">
              Uno solo por pantalla es cyan:{" "}
              <b className="text-foreground">el que cierra el trabajo</b>. Lo
              destructivo va en rojo y nunca queda pegado a él.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-semibold text-[11px] text-muted-foreground uppercase tracking-wider">
              Flujo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center overflow-hidden rounded-md border text-[12px]">
              {["Recibido", "Por decidir", "Conciliado"].map((e, i) => (
                <div
                  key={e}
                  className={
                    i === 1
                      ? "flex-1 bg-cyan-500 px-3 py-2 text-center font-medium text-white"
                      : "flex-1 px-3 py-2 text-center text-muted-foreground"
                  }
                >
                  {e}
                </div>
              ))}
            </div>
            <p className="text-[12.5px] text-muted-foreground">
              El camino completo a la vista, con la etapa actual marcada. Se
              entiende <b className="text-foreground">cuánto falta</b>, no solo
              dónde está.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-semibold text-[11px] text-muted-foreground uppercase tracking-wider">
              Tipografía
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="font-bold text-3xl tabular-nums tracking-tight">
              S/ 18,400.00
            </div>
            <p className="text-[12px] text-muted-foreground">
              La del sistema, sin instalar nada. Los importes en cifras de ancho
              igual, para que las columnas se alineen.
            </p>
            <div className="font-mono text-[13px]">4471835 · 4471832</div>
            <p className="text-[12.5px] text-muted-foreground">
              Los números de operación van en{" "}
              <b className="text-foreground">letra de ancho fijo</b>: es lo que
              permite ver de un vistazo que dos se diferencian en un solo
              dígito.
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
